import { useState, forwardRef, InputHTMLAttributes } from 'react';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * PasswordInput Component
 * 
 * A password input field with a toggle button to show/hide the password.
 * Features an eye icon that changes based on visibility state.
 * 
 * @example
 * <PasswordInput
 *   id="password"
 *   value={password}
 *   onChange={(e) => setPassword(e.target.value)}
 *   placeholder="Votre mot de passe"
 * />
 */
interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Additional CSS classes for the input */
  className?: string;
  /** Error state to show red border */
  hasError?: boolean;
  /** Success state to show green border */
  hasSuccess?: boolean;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className = '', hasError = false, hasSuccess = false, disabled, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const { t } = useTranslation();

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    // Build input classes
    let inputClasses = 'input pr-12 ';
    if (hasError) {
      inputClasses += 'border-red-500 focus:border-red-500 focus:ring-red-500 ';
    } else if (hasSuccess) {
      inputClasses += 'border-green-500 focus:border-green-500 focus:ring-green-500 ';
    }
    inputClasses += className;

    return (
      <div className="relative">
        <input
          ref={ref}
          type={showPassword ? 'text' : 'password'}
          className={inputClasses}
          disabled={disabled}
          {...props}
        />
        <button
          type="button"
          onClick={togglePasswordVisibility}
          disabled={disabled}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label={showPassword ? t('common.hidePassword') : t('common.showPassword')}
          tabIndex={-1}
        >
          {showPassword ? (
            // Eye-off icon (password visible)
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
              />
            </svg>
          ) : (
            // Eye icon (password hidden)
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          )}
        </button>
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
